import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NumberUtils } from '@pk/powerkioskutils';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { GraphqlService } from 'src/app/graphql/graphql.service';
import { Product, RfqSession, RfqSessionBid } from 'src/app/shared/models';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';
import { ConvertBidUnitsPipe } from 'src/app/shared/pipes/convert-bid-units.pipe';

@Component({
	selector: 'pk-broker-auction-bid-edit',
	templateUrl: './auction-bid-edit.component.html',
	styleUrl: './auction-bid-edit.component.scss',
	standalone: false,
})
export class AuctionBidEditComponent extends AbstractPageForm {
	@Input() bid: RfqSessionBid;
	@Input() rfqSession: RfqSession;

	@Output() bidUpdated = new EventEmitter<void>();

	public product: Product;
	public rfqSessionBid: RfqSessionBid;
	public bidMeasureUnits: { text: string; multiplier: number }[];

	constructor(
		private graphqlService: GraphqlService,
		private fb: FormBuilder,
		private toastrService: ToastrService,
		private convertBidUnitsPipe: ConvertBidUnitsPipe,
	) {
		super();
		this.submitText = 'Update Bid';
		this.submitAudit = 'auction-bid-edit:submitBid';
	}

	public override async loadPageData() {
		try {
			this.bidMeasureUnits = this.CONSTANTS.units[this.rfqSession.contract.serviceTypeId];
			const result = await this.graphqlService.getRfqSessionEditBidData(this.bid.rfqSessionBidId, this.bid.rfqSessionProduct.productId);
			this.rfqSessionBid = result.data.rfqSessionBid;
			this.product = result.data.product;
			this.product.pricingComponents = _.sortBy(this.product.pricingComponents, 'name');
		} catch (e) {
			console.log(e);
		}
	}

	public getForm() {
		return this.fb.group({
			units: [
				this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units) ?? this.bidMeasureUnits[0],
				[Validators.required],
			],
			bidAmount: [
				NumberUtils.rateToUnits(
					this.totalBidRate(this.rfqSessionBid),
					this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1],
				),
				[Validators.required, Validators.min(0)],
			],
			pricingComponents: this.fb.array(
				this.product.pricingComponents.map(pc =>
					this.fb.group({
						pricingComponentId: pc.id,
						amount: NumberUtils.rateToUnits(
							this.rfqSessionBid.pricingComponents.find(b => pc.id === b.pricingComponentId)?.amount,
							this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1],
						),
					}),
				),
			),
		});
	}

	public override async onFormSubmitted() {
		try {
			const dto = this.rfqSessionBidDto();
			await this.graphqlService.updateRfqSessionBid(this.bid.rfqSessionBidId, dto);
			this.toastrService.success('Bid updated successfully');
			this.bidUpdated.emit();
		} catch (e) {
			this.toastrService.warning(
				// eslint-disable-next-line max-len, max-len
				'There was a problem updating the bid. We have been notified and are working to fix the issue. Please check back again in 30 minutes.',
				'Pricing Session',
			);
			this.userAuditService.captureEvent(this.CONSTANTS.userAuditTypes.code, e.message, 'auction-bid-edit:submitBid');
		}
	}

	get margin() {
		return this.convertBidUnitsPipe.transform(this.rfqSession.contract.getTotalMargin(true, true, false), this.form.units.value.text);
	}

	public totalBidRate(bid: RfqSessionBid) {
		return Math.round((bid.amount + this.rfqSession.contract.getTotalMargin(true, true, false)) * 1e12) / 1e12;
	}

	private rfqSessionBidDto() {
		const dto: any = {
			pricingComponents: this.form.pricingComponents
				.map(pc => ({
					pricingComponentId: pc.pricingComponentId.value,
					amount: NumberUtils.rateToTherms(Number(pc.amount.value), this.form.units.value.text.split('/')[1]),
					units: this.form.units.value.text,
				}))
				.filter(pc => !_.isNil(pc.amount) && !_.isNaN(pc.amount) && pc.amount !== 0),
		};

		const bidAmount = NumberUtils.rateToTherms(Number(this.form.bidAmount.value), this.form.units.value.text.split('/')[1]);
		if (bidAmount !== this.totalBidRate(this.rfqSessionBid)) {
			dto.amount = Math.round((bidAmount - this.rfqSession.contract.getTotalMargin(true, true, false)) * 1e12) / 1e12;
			dto.rfqSessionBidStatusId = this.CONSTANTS.rfqSessionBidStatuses.edited;
		}

		return dto;
	}
}
